import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Modal, Button } from '../../ui';

// Expected columns mapping
const COLUMN_MAPPINGS = {
  name: ['name', 'branch name', 'branch_name', 'branchname'],
  contactPerson: ['contact person', 'contact_person', 'contactperson', 'contact'],
  contactNumber: ['contact number', 'contact_number', 'contactnumber', 'phone', 'telephone'],
  emailAddress: ['email', 'email address', 'email_address', 'emailaddress'],
  address: ['address', 'street address', 'street_address'],
  city: ['city', 'town'],
  province: ['province', 'state', 'region'],
  postalCode: ['postal code', 'postal_code', 'postalcode', 'zip', 'zipcode'],
  country: ['country'],
  hasFirewall: ['firewall', 'has firewall', 'has_firewall', 'hasfirewall'],
  numberOfUsers: ['users', 'number of users', 'numberofusers', 'user count', 'usercount'],
  numberOfDevices: ['devices', 'number of devices', 'numberofdevices', 'device count'],
  numberOfNetworkDevices: ['network devices', 'network_devices', 'networkdevices', 'routers', 'switches'],
  numberOfServers: ['servers', 'number of servers', 'numberofservers', 'server count'],
};

// Find matching column
const findColumnMatch = (header) => {
  const normalizedHeader = header.toLowerCase().trim();
  for (const [field, aliases] of Object.entries(COLUMN_MAPPINGS)) {
    if (aliases.includes(normalizedHeader)) {
      return field;
    }
  }
  return null;
};

// Parse boolean value
const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    return ['true', 'yes', '1', 'y'].includes(lower);
  }
  return Boolean(value);
};

// Parse numeric value
const parseNumber = (value) => {
  const num = parseInt(value, 10);
  return isNaN(num) ? 0 : num;
};

const BulkImportModal = ({ isOpen, onClose, onImport }) => {
  const [parsedData, setParsedData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [headers, setHeaders] = useState([]);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState(1); // 1: upload, 2: map columns, 3: preview

  // Reset state
  const resetState = () => {
    setParsedData([]);
    setColumnMapping({});
    setHeaders([]);
    setErrors([]);
    setStep(1);
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;

    setErrors([]);

    const fileExtension = uploadedFile.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      // Parse CSV
      Papa.parse(uploadedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.data.length === 0) {
            setErrors(['No data found in the file']);
            return;
          }
          processData(results.data, Object.keys(results.data[0]));
        },
        error: (error) => {
          setErrors([`Error parsing CSV: ${error.message}`]);
        },
      });
    } else if (['xlsx', 'xls'].includes(fileExtension)) {
      // Parse Excel
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet);
          
          if (data.length === 0) {
            setErrors(['No data found in the file']);
            return;
          }
          processData(data, Object.keys(data[0]));
        } catch (error) {
          setErrors([`Error parsing Excel: ${error.message}`]);
        }
      };
      reader.readAsArrayBuffer(uploadedFile);
    } else {
      setErrors(['Unsupported file format. Please use CSV or Excel (.xlsx, .xls)']);
    }
  }, []);

  // Process data and auto-map columns
  const processData = (data, fileHeaders) => {
    setHeaders(fileHeaders);
    setParsedData(data);

    // Auto-map columns
    const autoMapping = {};
    fileHeaders.forEach((header) => {
      const match = findColumnMatch(header);
      if (match) {
        autoMapping[header] = match;
      }
    });
    setColumnMapping(autoMapping);
    setStep(2);
  };

  // Handle column mapping change
  const handleMappingChange = (header, field) => {
    setColumnMapping((prev) => ({
      ...prev,
      [header]: field || undefined,
    }));
  };

  // Convert parsed data to branches
  const convertToBranches = () => {
    const branches = parsedData.map((row, index) => {
      const branch = {
        id: `imported_${Date.now()}_${index}`,
        name: '',
        contactPerson: '',
        contactNumber: '',
        emailAddress: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
        hasFirewall: false,
        numberOfUsers: 0,
        numberOfDevices: 0,
        numberOfNetworkDevices: 0,
        numberOfServers: 0,
      };

      // Apply mappings
      Object.entries(columnMapping).forEach(([header, field]) => {
        if (field && row[header] !== undefined) {
          if (field === 'hasFirewall') {
            branch[field] = parseBoolean(row[header]);
          } else if (['numberOfUsers', 'numberOfDevices', 'numberOfNetworkDevices', 'numberOfServers'].includes(field)) {
            branch[field] = parseNumber(row[header]);
          } else {
            branch[field] = String(row[header] || '').trim();
          }
        }
      });

      return branch;
    });

    return branches.filter((b) => b.name); // Filter out branches without names
  };

  // Proceed to preview
  const handleProceedToPreview = () => {
    const branches = convertToBranches();
    if (branches.length === 0) {
      setErrors(['No valid branches found. Make sure the "name" column is mapped.']);
      return;
    }
    setStep(3);
  };

  // Handle import
  const handleImport = () => {
    const branches = convertToBranches();
    onImport(branches);
    resetState();
    onClose();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const previewBranches = step === 3 ? convertToBranches() : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetState();
        onClose();
      }}
      title="Import Branches"
      size="lg"
    >
      <AnimatePresence mode="wait">
        {/* Step 1: Upload */}
        {step === 1 && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                ${isDragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }
              `}
            >
              <input {...getInputProps()} />
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {isDragActive ? 'Drop the file here...' : 'Drag & drop a file here, or click to select'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Supports CSV, Excel (.xlsx, .xls)
              </p>
            </div>

            {/* Download template */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  const template = 'name,email\nHead Office,branch@example.com';
                  const blob = new Blob([template], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'branch_import_template.csv';
                  a.click();
                }}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                Download template CSV
              </button>
              <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Template includes only branch name and email.
              </div>
            </div>

            {errors.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {errors.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 dark:text-red-400">{error}</p>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Step 2: Map columns */}
        {step === 2 && (
          <motion.div
            key="mapping"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Map columns from your file to branch fields. Green rows indicate auto-detected mappings.
            </p>

            <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">File Column</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Maps To</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Sample</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {headers.map((header) => (
                    <tr
                      key={header}
                      className={columnMapping[header] ? 'bg-green-50 dark:bg-green-900/10' : ''}
                    >
                      <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{header}</td>
                      <td className="px-4 py-2">
                        <select
                          value={columnMapping[header] || ''}
                          onChange={(e) => handleMappingChange(header, e.target.value)}
                          className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          <option value="">-- Skip --</option>
                          {Object.keys(COLUMN_MAPPINGS).map((field) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400 truncate max-w-[150px]">
                        {parsedData[0]?.[header]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {errors.length > 0 && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {errors.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 dark:text-red-400">{error}</p>
                ))}
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={handleProceedToPreview}>
                Preview Import
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {previewBranches.length} branch{previewBranches.length !== 1 ? 'es' : ''} will be imported
              </p>
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">
                Ready to import
              </span>
            </div>

            <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Name</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">City</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Users</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Devices</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Firewall</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {previewBranches.map((branch, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{branch.name}</td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{branch.city || '—'}</td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{branch.numberOfUsers}</td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{branch.numberOfDevices}</td>
                      <td className="px-4 py-2">
                        {branch.hasFirewall ? (
                          <span className="text-green-600 dark:text-green-400">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button variant="success" onClick={handleImport}>
                Import {previewBranches.length} Branch{previewBranches.length !== 1 ? 'es' : ''}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default BulkImportModal;

